// Player's preferred language, PERSISTED — CCT-279.
//
// ⚠️ THE INITIAL STATE IS NOT "ENGLISH", IT IS "NOT KNOWN YET". Reading the store is async:
// starting at `en` would render the home screen in English for a fraction of a second and then
// flip it — and, far worse, a filter applied during that interval would hide missions the
// player had chosen to see. `ready` says when the preference is known; the screen waits for it
// before filtering.
//
// ⚠️ AND THE EMPTY STRING IS A VALUE, NOT A MISSING ONE. It means "all languages" — the state
// the "Toutes" chip returns to. Two defects came from confusing it with "unset", and only an
// end-to-end run caught them, because each unit is right on its own:
//
//   1. the initial state was `DEFAULT_LANGUAGE`, so a first-time visitor arrived with an
//      English filter already on and saw 1 mission out of 14, with nothing on screen saying
//      a filter was active;
//   2. `setLanguage('')` fell back to `DEFAULT_LANGUAGE`, so the "Toutes" chip — whose whole
//      job is to LIFT the filter — silently set it to English instead.
//
// The default of English belongs to CONTENT resolution (`languageToServe`), which applies it
// on its own. It has no business filtering the catalogue.
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useCallback, useEffect, useState } from 'react';

import { normalizeLanguage } from '../utils/preferredLanguage';

const KEY = 'langue_preferee';

/** No filter: every language shown. Distinct from "not read yet", which `ready` reports. */
const ALL_LANGUAGES = '';

export function usePreferredLanguage() {
  const [langue, setLanguageState] = useState<string>(ALL_LANGUAGES);
  const [prete, setPrete] = useState(false);

  useEffect(() => {
    void (async () => {
      try {
        const v = await AsyncStorage.getItem(KEY);
        // An empty stored value is not a choice — but it IS a valid state ("all languages"),
        // so we simply keep what we already have rather than inventing a language.
        if (v && normalizeLanguage(v)) setLanguageState(normalizeLanguage(v));
      } catch {
        /* store unavailable: the default does the job, the screen must not fall over */
      } finally {
        setPrete(true);
      }
    })();
  }, []);

  const setLanguage = useCallback((v: string) => {
    // ⚠️ NO FALLBACK TO ENGLISH HERE. An empty value is the player asking for every language;
    // turning it into `en` would make the "lift the filter" button set a filter instead.
    const n = normalizeLanguage(v);
    // Applied BEFORE writing: the screen must react to the tap, not to the end of the write.
    setLanguageState(n);
    void AsyncStorage.setItem(KEY, n).catch(() => { /* best-effort */ });
  }, []);

  return { langue, setLanguage, prete };
}
